import { MarkerType } from "reactflow";

export const CAMPAIGN_TEMPLATES = [
  "Cold Outreach",
  "Re-engagement",
  "Event Follow-up",
  "Referral",
  "Renewal",
  "Custom",
];

/** Executable message / wait / control steps shown in the palette. */
export const WORKFLOW_PALETTE = [
  { type: "email", label: "Send email" },
  { type: "whatsapp", label: "WhatsApp message" },
  { type: "telegram", label: "Telegram message" },
  { type: "sms", label: "SMS message" },
  { type: "linkedin", label: "LinkedIn DM", hint: "Authoring supported; sending not enabled yet" },
  { type: "voice", label: "AI phone call", hint: "Authoring supported; calling not enabled yet" },
  { type: "wait", label: "Wait" },
  { type: "condition", label: "If / Else", hint: "Branch with If and Else paths" },
  { type: "ab", label: "A/B split", hint: "Pass-through in v1 (no branching yet)" },
  { type: "goal", label: "Goal" },
];

/** React Flow sourceHandle ids for If / Else branches. */
export const CONDITION_BRANCH_IF = "if";
export const CONDITION_BRANCH_ELSE = "else";

/** Goal nodes end the workflow — no outgoing edges / child nodes. */
export function isGoalWorkflowNodeType(type) {
  return String(type || "").toLowerCase() === "goal";
}

/**
 * Nodes that may have more than one outgoing edge (one per branch handle).
 * A/B split stays single-path until real A/B branching ships.
 */
export const BRANCHING_WORKFLOW_NODE_TYPES = new Set(["condition"]);

export function isBranchingWorkflowNodeType(type) {
  return BRANCHING_WORKFLOW_NODE_TYPES.has(String(type || "").toLowerCase());
}

export const MULTIPLE_OUTGOING_MESSAGE =
  "Each step can only continue to one next step. Use If / Else to branch into multiple paths.";

export const MULTIPLE_GOAL_NODES_MESSAGE =
  "A workflow can include only one Goal node. Remove the existing Goal before adding another.";

export function countGoalNodes(nodes = []) {
  return (nodes || []).filter((n) => isGoalWorkflowNodeType(n?.type)).length;
}

export function workflowHasGoalNode(nodes = []) {
  return countGoalNodes(nodes) > 0;
}

export const CONDITION_BRANCH_HANDLES = [
  { id: CONDITION_BRANCH_IF, label: "If", color: "#10B981" },
  { id: CONDITION_BRANCH_ELSE, label: "Else", color: "#94a3b8" },
];

export function conditionBranchLabel(handleId) {
  const key = String(handleId || "").toLowerCase();
  if (key === "if" || key === "yes") return "If";
  if (key === "else" || key === "no") return "Else";
  return "";
}

/** Channels that require message content before activation. */
export const MESSAGE_STEP_TYPES = new Set([
  "email", "whatsapp", "telegram", "sms", "linkedin", "voice",
]);

export const PERSONALIZATION_VARS = [
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "company_name", label: "Company" },
  { key: "job_title", label: "Job title" },
  { key: "project_name", label: "Project" },
  { key: "city", label: "City" },
  { key: "country", label: "Country" },
  { key: "assigned_rep", label: "Sales rep" },
];

const CHANNEL_LABELS = {
  email: "Send email",
  linkedin: "LinkedIn DM",
  whatsapp: "WhatsApp message",
  sms: "SMS message",
  telegram: "Telegram message",
  voice: "AI phone call",
};

const DEFAULT_MESSAGE = "Hi {{first_name}}, wanted to share a quick note.";

export function defaultWorkflow(channels = ["email"]) {
  const sendable = (channels || []).find((ch) => CHANNEL_LABELS[ch]) || "email";
  const first = sendable;
  const data = {
    label: CHANNEL_LABELS[first] || "Send message",
    message: "Hi {{first_name}}, wanted to share a quick note.",
  };
  if (first === "email") {
    data.subject = "Quick intro";
  }
  return {
    nodes: [
      {
        id: "start",
        type: "start",
        position: { x: 40, y: 40 },
        deletable: false,
        data: { label: "Enrollment", trigger: "Manual enrollment" },
      },
      {
        id: "step-1",
        type: first,
        position: { x: 40, y: 160 },
        data,
      },
    ],
    edges: [{ id: "e-start-1", source: "start", target: "step-1" }],
  };
}

/** Visual radius of workflow handles (`!w-2.5` → 10px). Tip sits on this rim. */
export const EDGE_HANDLE_RADIUS = 2;

/** Length of the custom arrowhead along the connector. */
export const EDGE_ARROW_LENGTH = 9;

/**
 * Move a point outward from a node handle along the handle normal.
 * Positions: top / bottom / left / right (React Flow Position).
 */
export function offsetHandlePoint(x, y, position, offset = EDGE_HANDLE_RADIUS) {
  const pos = String(position || "").toLowerCase();
  if (pos === "top") return { x, y: y - offset };
  if (pos === "bottom") return { x, y: y + offset };
  if (pos === "left") return { x: x - offset, y };
  if (pos === "right") return { x: x + offset, y };
  return { x, y };
}

/**
 * SVG polygon points for an arrowhead. Tip at (tipX, tipY) points toward (toX, toY).
 */
export function arrowHeadPoints(tipX, tipY, toX, toY, size = EDGE_ARROW_LENGTH) {
  const dx = toX - tipX;
  const dy = toY - tipY;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const baseX = tipX - ux * size;
  const baseY = tipY - uy * size;
  const half = size * 0.55;
  return [
    `${tipX},${tipY}`,
    `${baseX + px * half},${baseY + py * half}`,
    `${baseX - px * half},${baseY - py * half}`,
  ].join(" ");
}

/** @deprecated Use EDGE_HANDLE_RADIUS — kept so older imports keep working. */
export const EDGE_HANDLE_CLEARANCE = EDGE_HANDLE_RADIUS;

/** Kept for tests / compatibility; RemovableEdge draws its own arrowhead. */
export function workflowEdgeMarker(color = "#94a3b8") {
  return {
    type: MarkerType.ArrowClosed,
    color,
    width: 12,
    height: 12,
    markerUnits: "userSpaceOnUse",
  };
}

export function normalizeEdges(edges = []) {
  return edges.map((e) => {
    const handle = e.sourceHandle || e.data?.branch || "";
    const branchLabel = conditionBranchLabel(handle);
    let stroke = e.style?.stroke || "#94a3b8";
    if (handle === CONDITION_BRANCH_IF || handle === "yes") {
      stroke = "#10B981";
    } else if (handle === CONDITION_BRANCH_ELSE || handle === "no") {
      stroke = "#94a3b8";
    }
    return {
      ...e,
      type: e.type || "removable",
      label: e.label || branchLabel || undefined,
      data: {
        ...e.data,
        branch: e.data?.branch || (branchLabel ? String(handle).toLowerCase() : undefined),
      },
      style: {
        ...e.style,
        stroke,
      },
      // Disable SVG markers — custom polygon in RemovableEdge avoids handle overlap.
      markerEnd: undefined,
    };
  });
}

export const WORKFLOW_DRAG_MIME = "application/reactflow";

export const WORKFLOW_GRID_SIZE = 18;

export const WORKFLOW_NODE_SIZE = { width: 250, height: 72 };

export function nextNodeId() {
  return `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function snapToGrid(position, gridSize = WORKFLOW_GRID_SIZE) {
  return {
    x: Math.round(position.x / gridSize) * gridSize,
    y: Math.round(position.y / gridSize) * gridSize,
  };
}

/** Nudge drop position so new nodes don't sit directly on top of existing ones. */
export function resolveDropPosition(position, nodes, size = WORKFLOW_NODE_SIZE) {
  let pos = snapToGrid(position);
  for (let attempt = 0; attempt < 24; attempt += 1) {
    const blocked = nodes.some((node) => {
      const dx = Math.abs((node.position?.x || 0) - pos.x);
      const dy = Math.abs((node.position?.y || 0) - pos.y);
      return dx < size.width * 0.75 && dy < size.height * 0.85;
    });
    if (!blocked) return pos;
    pos = snapToGrid({ x: pos.x + size.width * 0.35, y: pos.y + size.height * 0.4 });
  }
  return pos;
}

export function centerDropOnCursor(flowPosition, size = WORKFLOW_NODE_SIZE) {
  return {
    x: flowPosition.x - size.width / 2,
    y: flowPosition.y - size.height / 2,
  };
}

export function createPaletteNode(type, label, position, nodes = []) {
  const resolved = resolveDropPosition(position, nodes);
  const data = {
    label: label || CHANNEL_LABELS[type] || type,
    justAdded: true,
  };
  if (type === "wait") {
    data.wait_days = 2;
    data.wait_hours = 0;
    data.wait_minutes = 0;
    data.label = waitLabelFromDuration(data);
  } else if (type === "condition") {
    data.condition = "";
  } else if (MESSAGE_STEP_TYPES.has(type)) {
    // Seed editable defaults so activation validation has a starting point;
    // users still configure content in the step inspector.
    data.message = type === "voice"
      ? "Call objective: introduce Sellyx and qualify interest in {{project_name}}."
      : DEFAULT_MESSAGE;
    if (type === "email") {
      data.subject = "Quick intro";
    }
  }
  return {
    id: nextNodeId(),
    type,
    position: resolved,
    data,
  };
}

function nodeMessageText(data = {}) {
  return String(data.message || data.body || data.body_text || "").trim();
}

function checkLabelIssue(node, type) {
  const data = node.data || {};
  const label = (data.label || "").trim();
  if (!label) {
    return {
      nodeId: node.id,
      type,
      label: type,
      field: "label",
      message: `${type} step: a step name is required.`,
    };
  }
  return null;
}

function checkMessageIssue(node, type, displayLabel) {
  const data = node.data || {};
  const message = nodeMessageText(data);
  if (!message) {
    const field = type === "voice" ? "call script / prompt" : "message content";
    return {
      nodeId: node.id,
      type,
      label: displayLabel,
      field,
      message: `${displayLabel}: ${field} is required before the workflow can be activated.`,
    };
  }
  return null;
}

function checkSubjectIssue(node, type, displayLabel) {
  const data = node.data || {};
  if (type === "email" && !String(data.subject || "").trim()) {
    return {
      nodeId: node.id,
      type,
      label: displayLabel,
      field: "subject",
      message: `${displayLabel}: email subject is required before the workflow can be activated.`,
    };
  }
  return null;
}

/**
 * Client-side content checks mirroring backend validate_workflow message rules.
 * Returns [{ nodeId, type, label, field, message }].
 */
export function findIncompleteMessageSteps(nodes = []) {
  const issues = [];
  for (const node of nodes) {
    const type = (node?.type || "").toLowerCase();
    const isMessage = MESSAGE_STEP_TYPES.has(type);
    const isWait = type === "wait";
    if (!isMessage && !isWait) continue;

    const labelIssue = checkLabelIssue(node, type);
    if (labelIssue) issues.push(labelIssue);

    if (!isMessage) continue;

    const displayLabel = (node.data?.label || "").trim() || type;

    const msgIssue = checkMessageIssue(node, type, displayLabel);
    if (msgIssue) issues.push(msgIssue);

    const subjectIssue = checkSubjectIssue(node, type, displayLabel);
    if (subjectIssue) issues.push(subjectIssue);
  }
  return issues;
}

function getReachableNodeIds(startId, nodeIds, edges) {
  const outgoing = {};
  for (const id of nodeIds) outgoing[id] = [];
  for (const edge of edges || []) {
    const src = String(edge?.source || "");
    const tgt = String(edge?.target || "");
    if (nodeIds.has(src) && nodeIds.has(tgt)) {
      outgoing[src].push(tgt);
    }
  }

  const reachable = new Set();
  const stack = [startId];
  while (stack.length) {
    const nid = stack.pop();
    if (!reachable.has(nid)) {
      reachable.add(nid);
      for (const next of outgoing[nid] || []) stack.push(next);
    }
  }
  return reachable;
}

/** True when the workflow has no Goal node (required end state). */
export function findMissingGoalNode(nodes = []) {
  const hasGoal = workflowHasGoalNode(nodes);
  if (hasGoal) return [];
  return [{
    nodeId: null,
    message: "Workflow must include a Goal node as the end of the path before you can save or publish.",
  }];
}

/** True when more than one Goal node exists. */
export function findMultipleGoalNodes(nodes = []) {
  const goals = (nodes || []).filter((n) => isGoalWorkflowNodeType(n?.type));
  if (goals.length <= 1) return [];
  return goals.slice(1).map((node) => ({
    nodeId: node.id,
    type: "goal",
    label: (node.data?.label || "Goal").trim() || "Goal",
    message: MULTIPLE_GOAL_NODES_MESSAGE,
  }));
}

/**
 * Steps not reachable from Start — blocks save (mirrors backend orphan_node).
 */
export function findDisconnectedWorkflowSteps(nodes = [], edges = []) {
  const nodeList = (nodes || []).filter((n) => n?.id);
  if (!nodeList.length) {
    return [{
      nodeId: null,
      message: "Workflow has no nodes.",
    }];
  }
  const nodeIds = new Set(nodeList.map((n) => String(n.id)));
  const start = nodeList.find((n) => (n.type || "").toLowerCase() === "start");
  if (!start) {
    return [{
      nodeId: null,
      message: "Workflow must include a start node.",
    }];
  }

  const reachable = getReachableNodeIds(String(start.id), nodeIds, edges);

  const issues = [];
  for (const node of nodeList) {
    const nid = String(node.id);
    if (reachable.has(nid)) continue;
    const type = (node.type || "").toLowerCase();
    const label = (node.data?.label || type).trim() || "Untitled step";
    issues.push({
      nodeId: nid,
      type,
      label,
      message: `"${label}" is not connected to the main workflow. Connect it to Workflow or remove it before saving.`,
    });
  }
  return issues;
}

const WORKFLOW_CYCLE_MESSAGE =
  "This connection would create a loop. Steps cannot connect back to an earlier step in the path.";

/** Build adjacency from edges (source → targets). */
function workflowAdjacency(edges = []) {
  const adj = {};
  for (const edge of edges || []) {
    const src = String(edge?.source || "");
    const tgt = String(edge?.target || "");
    if (!src || !tgt) continue;
    if (!adj[src]) adj[src] = [];
    adj[src].push(tgt);
  }
  return adj;
}

/**
 * True if adding source → target would close a directed cycle.
 * Multiple incoming edges (merges) are fine; multiple outgoing from linear
 * nodes are blocked separately via the canvas handle and backend validation.
 */
export function edgeWouldCreateCycle(edges, source, target) {
  const src = String(source || "");
  const tgt = String(target || "");
  if (!src || !tgt) return false;
  if (src === tgt) return true;
  const adj = workflowAdjacency(edges);
  const stack = [tgt];
  const seen = new Set();
  while (stack.length) {
    const nid = stack.pop();
    if (nid === src) return true;
    if (seen.has(nid)) continue;
    seen.add(nid);
    for (const next of adj[nid] || []) stack.push(next);
  }
  return false;
}

function detectCycleNodeId(nodeIds, outgoing) {
  const state = {};
  for (const id of nodeIds) state[id] = 0;

  let cycleNodeId = null;
  const dfs = (nid) => {
    state[nid] = 1;
    for (const next of outgoing[nid] || []) {
      if (state[next] === 1) {
        cycleNodeId = next;
        return true;
      }
      if (state[next] === 0 && dfs(next)) return true;
    }
    state[nid] = 2;
    return false;
  };

  for (const nid of nodeIds) {
    if (state[nid] === 0 && dfs(nid)) break;
  }
  return cycleNodeId;
}

/** Detect any cycle in the workflow graph (for save validation). */
export function findWorkflowCycleIssues(nodes = [], edges = []) {
  const nodeIds = new Set(
    (nodes || []).filter((n) => n?.id).map((n) => String(n.id)),
  );
  if (!nodeIds.size) return [];

  const outgoing = {};
  for (const id of nodeIds) outgoing[id] = [];
  for (const edge of edges || []) {
    const src = String(edge?.source || "");
    const tgt = String(edge?.target || "");
    if (nodeIds.has(src) && nodeIds.has(tgt)) outgoing[src].push(tgt);
  }

  const cycleNodeId = detectCycleNodeId(nodeIds, outgoing);
  if (!cycleNodeId) return [];

  return [{
    nodeId: cycleNodeId,
    message: WORKFLOW_CYCLE_MESSAGE,
  }];
}

export function markIncompleteMessageSteps(nodes = []) {
  const incompleteIds = new Set(findIncompleteMessageSteps(nodes).map((i) => i.nodeId));
  return nodes.map((node) => {
    const incomplete = incompleteIds.has(node.id);
    if (!incomplete && !node.data?.configIncomplete) return node;
    return {
      ...node,
      data: {
        ...node.data,
        configIncomplete: incomplete,
      },
    };
  });
}

/** Replace {{var}} tokens with sample values for preview. */
export function previewPersonalizedMessage(text = "", samples = {}) {
  const defaults = {
    first_name: "Alex",
    last_name: "Rivera",
    company: "Acme Builders",
    project_name: "Harbor Tower",
    city: "Dubai",
    country: "UAE",
    assigned_rep: "Jordan Lee",
    ...samples,
  };
 return String(text).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (
  defaults[key] != null ? String(defaults[key]) : `{{${key}}}`
));
}

/** Human-readable wait duration from node data (engine defaults to 2 days if empty). */
export const DEFAULT_WAIT_DURATION = Object.freeze({
  wait_days: 2,
  wait_hours: 0,
  wait_minutes: 0,
});

/** Clamp wait fields; if days+hours+minutes are all 0, reset to the 2-day default. */
export function normalizeWaitDurationFields(current = {}, patch = {}) {
  const next = {
    wait_days: Math.max(0, Math.min(365, Number(
      patch.wait_days !== undefined ? patch.wait_days : current.wait_days,
    ) || 0)),
    wait_hours: Math.max(0, Math.min(23, Number(
      patch.wait_hours !== undefined ? patch.wait_hours : current.wait_hours,
    ) || 0)),
    wait_minutes: Math.max(0, Math.min(59, Number(
      patch.wait_minutes !== undefined ? patch.wait_minutes : current.wait_minutes,
    ) || 0)),
  };
  if (!next.wait_days && !next.wait_hours && !next.wait_minutes) {
    return { ...DEFAULT_WAIT_DURATION, resetFromZero: true };
  }
  return { ...next, resetFromZero: false };
}

export function formatWaitDuration(data = {}) {
  const days = Math.max(0, Number(data.wait_days) || 0);
  const hours = Math.max(0, Number(data.wait_hours) || 0);
  const minutes = Math.max(0, Number(data.wait_minutes) || 0);
  const parts = [];
  if (days) parts.push(`${days} day${days === 1 ? "" : "s"}`);
  if (hours) parts.push(`${hours} hr${hours === 1 ? "" : "s"}`);
  if (minutes) parts.push(`${minutes} min`);
  if (!parts.length) return "2 days (default)";
  return parts.join(" ");
}

export function waitLabelFromDuration(data = {}) {
  const text = formatWaitDuration(data);
  if (text.includes("default")) return "Wait 2 days";
  return `Wait ${text}`;
}

export function encodeWorkflowDragPayload(type, label) {
  return JSON.stringify({ type, label });
}

export function decodeWorkflowDragPayload(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.type) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function stripWorkflowUiState(nodes = []) {
  return nodes.map((node) => {
    if (!node.data) return node;
    const { justAdded, configIncomplete, hasOutgoingEdge, ...data } = node.data;
    if (justAdded === undefined && configIncomplete === undefined && hasOutgoingEdge === undefined) return node;
    return { ...node, data };
  });
}

/** Start node cannot be deleted; other steps can. */
export function withWorkflowNodeFlags(nodes = []) {
  return nodes.map((node) => {
    const isStart = node.id === "start" || (node.type || "").toLowerCase() === "start";
    return { ...node, deletable: !isStart };
  });
}

/** Remove a workflow step and any edges touching it (never removes start). */
export function removeWorkflowNode(nodeId, nodes = [], edges = []) {
  const id = (nodeId || "").trim();
  if (!id || id === "start") return null;
  const target = nodes.find((n) => n.id === id);
  if (!target || target.type === "start") return null;
  const nextNodes = nodes.filter((n) => n.id !== id);
  const nextEdges = edges.filter((e) => e.source !== id && e.target !== id);
  return { nodes: withWorkflowNodeFlags(nextNodes), edges: nextEdges };
}

export function statusLabel(status) {
  const map = {
    draft: "Draft",
    active: "Active",
    paused: "Paused",
    archived: "Archived",
  };
  return map[status] || status;
}

/** True when create-wizard progress is saved but not finished. */
export function isWizardInProgress(campaignOrSettings) {
  const settings = campaignOrSettings?.settings && typeof campaignOrSettings.settings === "object"
    ? campaignOrSettings.settings
    : campaignOrSettings;
  if (!settings || typeof settings !== "object") return false;
  const wizard = settings.wizard;
  return Boolean(wizard && typeof wizard === "object" && wizard.complete === false);
}

/** Dashboard / editor display label (Creation In Progress overrides Draft). */
export function campaignStatusDisplayLabel(campaign) {
  if (isWizardInProgress(campaign) && String(campaign?.status || "").toLowerCase() === "draft") {
    return "Creation In Progress";
  }
  return statusLabel(campaign?.status);
}

export function statusClass(status) {
  if (status === "active") return "bg-emerald-50 text-emerald-700";
  if (status === "paused") return "bg-amber-50 text-amber-700";
  if (status === "draft") return "bg-blue-50 text-blue-600";
  if (status === "archived") return "bg-slate-200 text-slate-700";
  return "bg-slate-100 text-slate-600";
}

export function humanizeWorkflowError(e) {
  const raw = String(e?.message || e?.code || "").trim();
  if (
    e?.code === "orphan_node"
    || /not reachable from start/i.test(raw)
    || /^Node ['"]n-/i.test(raw)
  ) {
    if (/not connected to the main workflow/i.test(raw)) return raw;
    return (
      "Some steps are not connected to the main workflow. "
      + "Connect them to Enrollment or remove them before publishing."
    );
  }
  if (e?.code === "workflow_cycle") {
    return raw || "Workflow contains a loop. Remove the connection that loops back to an earlier step.";
  }
  if (e?.code === "multiple_outgoing") {
    return raw || MULTIPLE_OUTGOING_MESSAGE;
  }
  return raw;
}

/** Format publish 409 workflow_invalid errors for toasts. */
function _getWorkflowErrors(err) {
  const nested = err?.data?.detail?.error || err?.data?.error || {};
  return Array.isArray(nested.errors) ? nested.errors : [];
}

function _isOrphanError(e) {
  return e?.code === "orphan_node" || /not reachable from start/i.test(String(e?.message || ""));
}

export function formatWorkflowInvalidError(err) {
  const errors = _getWorkflowErrors(err);
  if (!errors.length) {
    return err?.message || "Workflow failed validation";
  }

  const lines = [];
  let orphanIncluded = false;
  for (const e of errors) {
    if (_isOrphanError(e)) {
      if (orphanIncluded) continue;
      orphanIncluded = true;
    }
    const line = humanizeWorkflowError(e);
    if (line) lines.push(line);
    if (lines.length >= 4) break;
  }
  const more = errors.length > 4 ? ` (+${errors.length - 4} more)` : "";
  return lines.join("\n") + more;
}
